import type { ReactElement } from "react";
import { defineHFComponent } from "../component.js";
import { defineBrowserSetup } from "../runtime.js";

export type ConicProgressRingProps = {
  progress?: number;
  thickness?: number;
  label?: number;
  suffix?: string;
  color?: string;
  trackColor?: string;
  textColor?: string;
  size?: number;
  duration?: number;
};

function View({ progress = 100, thickness = 12, label = progress, suffix = "", color = "#35d6a0", trackColor = "#1b2938", textColor = "#f4f7fb", size = 520 }: ConicProgressRingProps): ReactElement {
  return (
    <div className="ring-stage" style={{ "--ring-size": `${size}px`, "--ring-thickness": `${Math.max(4, Math.min(30, thickness))}%`, "--ring-color": color, "--ring-track": trackColor, "--ring-ink": textColor } as React.CSSProperties}>
      <div className="ring" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)} />
      <div className="ring-label"><span>0</span>{suffix}</div>
    </div>
  );
}

export const ConicProgressRing = defineHFComponent<ConicProgressRingProps>({
  name: "conic-progress-ring",
  View,
  css: (scope) => `
    @property --ring-progress{syntax:"<percentage>";inherits:false;initial-value:0%}
    ${scope}{display:grid;place-items:center;color:var(--ring-ink,#f4f7fb);font-family:Inter,Arial,sans-serif}
    ${scope} .ring-stage{position:relative;width:var(--ring-size);height:var(--ring-size)}
    ${scope} .ring{--ring-progress:0%;position:absolute;inset:0;border-radius:50%;background:conic-gradient(from -90deg,var(--ring-color) 0 var(--ring-progress),var(--ring-track) var(--ring-progress) 100%);-webkit-mask:radial-gradient(farthest-side,rgba(255,255,255,0) calc(100% - var(--ring-thickness)),#fff 0);mask:radial-gradient(farthest-side,rgba(255,255,255,0) calc(100% - var(--ring-thickness)),#fff 0)}
    ${scope} .ring-label{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:calc(var(--ring-size)*.2647059);font-weight:700;line-height:1;letter-spacing:-.04em;font-variant-numeric:tabular-nums}
  `,
  setup: defineBrowserSetup(
    ({ progress = 100, label = progress, suffix = "", duration = 4 }) => ({
      progress: Math.max(0, Math.min(100, progress)),
      label,
      suffix,
      duration: Math.max(0.001, duration),
    }),
    ({ root, props, tl, gsap, offset }) => {
      const ring = root.querySelector(".ring");
      const stage = root.querySelector(".ring-stage");
      const label = root.querySelector(".ring-label");
      if (!ring || !stage || !label) throw new Error("ConicProgressRing markup is incomplete");

      const inBase = 1.4;
      const outBase = 0.5;
      const scale = props.duration < inBase + outBase ? props.duration / (inBase + outBase) : 1;
      const fillDuration = inBase * scale;
      const outDuration = outBase * scale;
      const outStart = fillDuration + Math.max(0, props.duration - fillDuration - outDuration);
      const updateLabel = () => {
        const fill = Number.parseFloat((ring as HTMLElement).style.getPropertyValue("--ring-progress")) || 0;
        const ratio = props.progress > 0 ? fill / props.progress : 1;
        label.textContent = `${Math.round(props.label * ratio)}${props.suffix}`;
        ring.setAttribute("aria-valuenow", String(Math.round(fill)));
      };

      gsap.set(stage, { opacity: 1 });
      gsap.set(ring, { "--ring-progress": "0%" });
      updateLabel();
      tl.to(ring, {
        "--ring-progress": `${props.progress}%`,
        duration: fillDuration,
        ease: "power2.out",
        onUpdate: updateLabel,
      }, offset);
      tl.to(stage, { opacity: 0, duration: outDuration, ease: "power2.in" }, offset + outStart);
    },
  ),
});
